/**
 * AerialCamera — admin-only cinematic orbit for streaming the lobby.
 *
 * When `store.aerialView` is on, this slowly rotates the camera 360° around the
 * cauldron/throne at a high vantage, framing the whole hall. ControlsEcctrl
 * passes `disableFollowCam` while it's active (see ControlsEcctrl.tsx), so the
 * ecctrl follow-cam stops writing the camera and this is the sole writer — no
 * render-priority juggling needed (default priority runs before PostFX's
 * composer render each frame).
 *
 * Orbit framing is tuned to stay inside the hall walls (half-width 14,
 * z ∈ [-8, 26]); adjust the constants below to reframe the shot.
 */
import { useFrame, useThree } from "@react-three/fiber";
import { useRef } from "react";

import { useStore } from "../store";

// Orbit centred on the cauldron (z ≈ 3.8) so the overflowing pot + throne sit
// in frame, with the camera path comfortably inside the walls.
const CENTER: [number, number, number] = [0, 2.0, 3.8];
const RADIUS = 9.5;
const HEIGHT = 8.5;
const ORBIT_SPEED = 0.12; // rad/s → ~52s per full revolution

export function AerialCamera(): null {
  const active = useStore((s) => s.aerialView);
  const camera = useThree((s) => s.camera);
  const angleRef = useRef(0);

  useFrame((_, dt) => {
    if (!active) return;
    // dt is the frame delta; TabRefocusGuard already swallows the post-tab-out
    // spike, so the orbit won't lurch on refocus.
    angleRef.current += dt * ORBIT_SPEED;
    const a = angleRef.current;
    camera.position.set(
      CENTER[0] + Math.cos(a) * RADIUS,
      HEIGHT,
      CENTER[2] + Math.sin(a) * RADIUS,
    );
    camera.lookAt(CENTER[0], CENTER[1], CENTER[2]);
  });

  return null;
}
